import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { apiPost, apiPut, apiDelete, apiGet } from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import Pagination from '@/components/Pagination';
import { WorkerPhoto } from '../hooks/useWorkerPhoto';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft,
  Users,
  DollarSign,
  Edit,
  Phone,
  Globe,
  Loader2,
  Check,
  X,
  AlertTriangle,
  Plane,
  Trash2,
  CreditCard,
  Minus,
  Plus,
  CheckCircle,
  FileText,
  Ticket,
  Key,
  Building2,
  Wallet,
  TrendingUp,
  TrendingDown,
  Eye,
  Search,
  Sparkles,
  Shield,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import WorkerDetailsModal from '@/components/WorkerDetailsModal';

// Helper function to format base64 image with proper data URI prefix

// ========== ADVANCED FUZZY SEARCH SYSTEM ==========

const normalizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text.toString().toLowerCase().normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s\u0600-\u06FF]/g, '').trim();
};

const levenshteinDistance = (str1, str2) => {
  const m = str1.length, n = str2.length;
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) dp[i][j] = dp[i - 1][j - 1];
      else dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
};

const calculateSimilarity = (str1, str2) => {
  if (!str1 || !str2) return 0;
  const norm1 = normalizeText(str1), norm2 = normalizeText(str2);
  if (!norm1 || !norm2) return 0;
  const maxLen = Math.max(norm1.length, norm2.length);
  if (maxLen === 0) return 100;
  return Math.round((1 - levenshteinDistance(norm1, norm2) / maxLen) * 100);
};

const fuzzyMatch = (text, query, threshold = 50) => {
  if (!text || !query) return { match: false, score: 0 };
  const normText = normalizeText(text), normQuery = normalizeText(query);
  if (!normText || !normQuery) return { match: false, score: 0 };
  if (normText.includes(normQuery)) return { match: true, score: 100 };
  
  const textWords = normText.split(/\s+/), queryWords = normQuery.split(/\s+/);
  let totalScore = 0, matchedWords = 0;
  
  for (const qWord of queryWords) {
    let bestWordScore = 0;
    for (const tWord of textWords) {
      if (tWord.startsWith(qWord)) bestWordScore = Math.max(bestWordScore, 95);
      else if (tWord.includes(qWord)) bestWordScore = Math.max(bestWordScore, 85);
      else {
        const similarity = calculateSimilarity(qWord, tWord);
        if (similarity >= threshold) bestWordScore = Math.max(bestWordScore, similarity);
      }
    }
    if (bestWordScore > 0) { totalScore += bestWordScore; matchedWords++; }
  }
  
  const finalScore = queryWords.length > 0 ? Math.round(totalScore / queryWords.length) : 0;
  return { match: matchedWords > 0 && finalScore >= threshold, score: finalScore };
};

const advancedSearch = (items, query, searchFields) => {
  if (!query || !query.trim()) return items.map(item => ({ ...item, searchScore: 0 }));
  
  return items.map(item => {
    let maxScore = 0;
    for (const field of searchFields) {
      const value = item[field.key];
      if (!value) continue;
      const result = fuzzyMatch(value, query, field.threshold || 50);
      const weightedScore = result.score * (field.weight || 1);
      if (result.match && weightedScore > maxScore) maxScore = weightedScore;
    }
    return { ...item, searchScore: maxScore };
  }).filter(item => item.searchScore > 0).sort((a, b) => b.searchScore - a.searchScore);
};

const highlightMatch = (text, query) => {
  if (!text || !query) return text;
  const normQuery = normalizeText(query);
  const words = normQuery.split(/\s+/).filter(w => w.length > 0);
  if (words.length === 0) return text;
  
  let result = text;
  for (const word of words) {
    const regex = new RegExp(`(${word})`, 'gi');
    result = result.replace(regex, '|||$1|||');
  }
  
  return result.split('|||').map((part, index) => {
    const isMatch = words.some(w => normalizeText(part) === w);
    if (isMatch) return <span key={index} className="bg-yellow-500/40 text-yellow-200 px-0.5 rounded">{part}</span>;
    return part;
  });
};

// ========== END SEARCH SYSTEM ==========

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

// Worker Card Component - NEW DESIGN with ALL FUNCTIONS PRESERVED
const WorkerCard = ({ worker, currencySymbol, onPayment, onDeduction, onStatusChange, onDelete, onFullyPaid, onSettle, onAmendment, onViewDetails, onWarrantyActivate, onWarrantyPeriodChange, searchQuery }) => {
  const initials = (worker.workerName || 'NA').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  
  // Highlight matched text if searching
  const renderHighlightedText = (text) => {
    if (!searchQuery || !text) return text;
    return highlightMatch(text, searchQuery);
  };
  const deductionsValue = worker.deductionsUSD || 0;
  const isLocked = worker.status === 'runaway' || worker.status === 'noarrival' || worker.status === 'settled';
  const canSettle = (worker.status === 'runaway' || worker.status === 'noarrival') && worker.status !== 'settled';
  const showFullyPaid = (worker.remainingAmount || 0) > 0;
  const hasAmendments = (worker.contractAmendments || []).length > 0;
  
  // Status config
  const statusConfig = {
    'arrived': { bg: 'from-emerald-500 to-emerald-600', text: 'ARRIVED', icon: Check, border: 'border-emerald-500/50', glow: 'shadow-emerald-500/20' },
    'abroad': { bg: 'from-blue-500 to-blue-600', text: 'ABROAD', icon: Plane, border: 'border-blue-500/50', glow: 'shadow-blue-500/20' },
    'runaway': { bg: 'from-red-500 to-red-600', text: 'RUNAWAY', icon: X, border: 'border-red-500/50', glow: 'shadow-red-500/20' },
    'noarrival': { bg: 'from-amber-500 to-amber-600', text: 'NO ARRIVAL', icon: AlertTriangle, border: 'border-amber-500/50', glow: 'shadow-amber-500/20' },
    'settled': { bg: 'from-purple-500 to-purple-600', text: 'SETTLED', icon: CheckCircle, border: 'border-purple-500/50', glow: 'shadow-purple-500/20' }
  };
  const config = statusConfig[worker.status] || statusConfig['abroad'];
  const StatusIcon = config.icon;
  
  return (
    <motion.div variants={itemVariants}>
      <div 
        className={`bg-slate-900/80 backdrop-blur-xl rounded-2xl overflow-hidden border-2 ${config.border} shadow-xl ${config.glow}`}
        data-testid={`worker-card-${worker.id}`}
      >
        {/* Top gradient line */}
        <div className={`h-1 bg-gradient-to-r ${config.bg}`} />
        
        {/* TADABER Header */}
        <div className="bg-slate-950/50 py-3 px-4 flex items-center justify-between border-b border-white/5">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center">
              <span className="text-white font-bold text-sm">T</span>
            </div>
            <span className="text-white text-sm font-bold tracking-wider">TADABER</span>
          </div>
          <div className={`flex items-center gap-1.5 px-3 py-1 rounded-lg bg-gradient-to-r ${config.bg}`}>
            <StatusIcon className="h-3.5 w-3.5 text-white" />
            <span className="text-white text-[11px] font-bold">{config.text}</span>
          </div>
        </div>

        {/* Worker Profile Section */}
        <div className="p-4">
          <div className="bg-slate-950/50 rounded-xl p-4 mb-4 border border-white/5">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-xl border-2 border-orange-500/50 overflow-hidden flex-shrink-0 shadow-lg shadow-orange-500/20 bg-slate-800 flex items-center justify-center">
                <WorkerPhoto
                  worker={worker}
                  alt={worker.workerName}
                  className="w-full h-full object-cover"
                  fallback={<span className="text-white font-bold text-xl">{initials}</span>}
                />
              </div>
              <div className="flex-1">
                {/* Worker Name - Full and Clear with Highlight */}
                <h3 className="text-white text-base font-bold leading-tight mb-1">
                  {renderHighlightedText((worker.workerName || 'N/A').toUpperCase())}
                </h3>
                <p className="text-orange-400 text-sm font-medium">
                  {renderHighlightedText((worker.nationality || 'Unknown').toUpperCase())}
                </p>
                {/* Search Score Badge */}
                {searchQuery && worker.searchScore > 0 && (
                  <div className="mt-1">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      worker.searchScore >= 90 ? 'bg-green-500/20 text-green-400' :
                      worker.searchScore >= 70 ? 'bg-cyan-500/20 text-cyan-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {worker.searchScore >= 90 ? '⭐ Exact Match' : 
                       worker.searchScore >= 70 ? '✓ Good Match' : 
                       '~ Partial Match'} ({Math.round(worker.searchScore)}%)
                    </span>
                  </div>
                )}
              </div>
              {/* View Details Button */}
              <Button
                onClick={() => onViewDetails(worker)}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-bold rounded-xl flex-shrink-0"
                size="sm"
                data-testid={`view-details-${worker.id}`}
              >
                <Eye className="h-4 w-4 mr-1" />
                View
              </Button>
            </div>
          </div>

          {/* Status Info Section */}
          <div className="bg-slate-950/50 rounded-xl p-4 mb-4 border border-white/5">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-white text-sm font-semibold">STATUS INFO</span>
              <span className="text-slate-500 text-xs">/ Status Details</span>
            </div>
            
            {/* Current Status */}
            <div className="flex items-center gap-2 mb-3 p-2 bg-slate-900/50 rounded-lg">
              <span className="text-cyan-400 text-sm">◆</span>
              <span className="text-slate-300 text-sm">
                {worker.status === 'arrived' ? (
                  <>Arrived {worker.arrivalDate && <span className="text-emerald-400 font-medium">({worker.arrivalDate})</span>}</>
                ) : worker.status === 'runaway' ? (
                  <>Runaway {worker.statusChangedAt && <span className="text-red-400 font-medium">({worker.statusChangedAt})</span>}</>
                ) : worker.status === 'noarrival' ? (
                  <>No Arrival {worker.statusChangedAt && <span className="text-amber-400 font-medium">({worker.statusChangedAt})</span>}</>
                ) : worker.status === 'settled' ? (
                  <>Debt Settled {worker.statusChangedAt && <span className="text-purple-400 font-medium">({worker.statusChangedAt})</span>}</>
                ) : (
                  'Currently Abroad / Outside Country'
                )}
              </span>
            </div>
            
            {/* Linked Data Badges */}
            <div className="flex gap-2 flex-wrap mb-3">
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${
                worker.visaNumber ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'
              }`}>
                <FileText className={`h-3.5 w-3.5 ${worker.visaNumber ? 'text-emerald-400' : 'text-red-400'}`} />
                <span className={`text-[11px] font-semibold ${worker.visaNumber ? 'text-emerald-400' : 'text-red-400'}`}>
                  {worker.visaNumber ? 'VISA ✓' : 'NO VISA'}
                </span>
              </div>
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${
                worker.linkedTicketId ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'
              }`}>
                <Ticket className={`h-3.5 w-3.5 ${worker.linkedTicketId ? 'text-emerald-400' : 'text-red-400'}`} />
                <span className={`text-[11px] font-semibold ${worker.linkedTicketId ? 'text-emerald-400' : 'text-red-400'}`}>
                  {worker.linkedTicketId ? 'TICKET ✓' : 'NO TICKET'}
                </span>
              </div>
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${
                worker.passportNumber ? 'bg-blue-500/10 border-blue-500/30' : 'bg-slate-500/10 border-slate-500/30'
              }`}>
                <Key className={`h-3.5 w-3.5 ${worker.passportNumber ? 'text-blue-400' : 'text-slate-500'}`} />
                <span className={`text-[11px] font-semibold ${worker.passportNumber ? 'text-blue-400' : 'text-slate-500'}`}>
                  {worker.passportNumber ? renderHighlightedText(worker.passportNumber) : 'NO PASSPORT'}
                </span>
              </div>
            </div>

            {/* Personal Details Grid */}
            <div className="grid grid-cols-2 gap-2 p-3 bg-slate-900/50 rounded-lg text-xs">
              {worker.dateOfBirth && (
                <div>
                  <span className="text-slate-500">DOB: </span>
                  <span className="text-white">{worker.dateOfBirth}</span>
                </div>
              )}
              {worker.passportExpiry && (
                <div>
                  <span className="text-slate-500">Passport Exp: </span>
                  <span className="text-white">{worker.passportExpiry}</span>
                </div>
              )}
              {worker.visaExpiryDate && (
                <div>
                  <span className="text-slate-500">Visa Exp: </span>
                  <span className="text-white">{worker.visaExpiryDate}</span>
                </div>
              )}
              {worker.profession && (
                <div>
                  <span className="text-slate-500">Profession: </span>
                  <span className="text-white">{worker.profession}</span>
                </div>
              )}
              {worker.sponsorName && (
                <div className="col-span-2">
                  <span className="text-slate-500">Sponsor: </span>
                  <span className="text-white">{worker.sponsorName}</span>
                </div>
              )}
            </div>
            
            {/* Status Change Control — free transitions between all states
                (abroad ↔ arrived, runaway/noarrival → anything). Settled stays locked. */}
            {worker.status !== 'settled' && (
              <div className="flex gap-2 flex-wrap justify-center mt-4" data-testid={`status-switch-${worker.id}`}>
                {[
                  { value: 'abroad', label: 'خارج الدولة', cls: 'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700', icon: Plane },
                  { value: 'arrived', label: 'داخل الدولة', cls: 'from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700', icon: Check },
                  { value: 'runaway', label: 'هروب', cls: 'from-red-500 to-red-600 hover:from-red-600 hover:to-red-700', icon: X },
                  { value: 'noarrival', label: 'لم تصل', cls: 'from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700', icon: AlertTriangle },
                ].filter((s) => s.value !== worker.status).map((s) => {
                  const SIcon = s.icon;
                  return (
                    <Button
                      key={s.value}
                      onClick={() => onStatusChange(worker.id, s.value)}
                      className={`bg-gradient-to-r ${s.cls} text-white text-xs font-bold rounded-lg`}
                      size="sm"
                      data-testid={`mark-${s.value}-${worker.id}`}
                    >
                      <SIcon className="h-3 w-3 mr-1" />
                      {s.label}
                    </Button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Financial Section */}
          <div className="bg-slate-950/50 rounded-xl p-4 mb-4 border border-white/5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-white text-sm font-semibold">FINANCIALS</span>
                <span className="text-slate-500 text-xs">/ Financial Details</span>
              </div>
              {/* Contract Amendment Button */}
              {!isLocked && (
                <Button
                  onClick={() => onAmendment(worker)}
                  variant="ghost"
                  size="sm"
                  className={`h-7 px-2 text-xs ${hasAmendments ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/10' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'}`}
                  data-testid={`amendment-btn-${worker.id}`}
                >
                  <FileText className="h-3.5 w-3.5 mr-1" />
                  {hasAmendments ? `${(worker.contractAmendments || []).length} Amendments` : 'Add Amendment'}
                </Button>
              )}
            </div>
            
            {/* Show original cost and amendments breakdown if amendments exist */}
            {hasAmendments && (
              <div className="mb-3 p-2 bg-amber-500/10 rounded-lg border border-amber-500/20">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Original Contract:</span>
                  <span className="text-white">{currencySymbol}{(worker.originalTotalCost || worker.totalCost || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-amber-400">+ Amendments:</span>
                  <span className="text-amber-400">+{currencySymbol}{(worker.totalAmendments || 0).toLocaleString()}</span>
                </div>
                <div className="border-t border-amber-500/20 mt-1.5 pt-1.5 flex justify-between text-xs font-bold">
                  <span className="text-white">Total Contract:</span>
                  <span className="text-white">{currencySymbol}{(worker.totalCost || 0).toLocaleString()}</span>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center p-3 bg-slate-900/50 rounded-xl">
                <p className="text-slate-500 text-[10px] mb-1">TOTAL</p>
                <p className="text-white text-lg font-bold">{currencySymbol}{(worker.totalCost || 0).toLocaleString()}</p>
              </div>
              <div className="text-center p-3 bg-slate-900/50 rounded-xl">
                <p className="text-slate-500 text-[10px] mb-1">PAID</p>
                <p className="text-emerald-400 text-lg font-bold">{currencySymbol}{(worker.paidAmount || 0).toLocaleString()}</p>
              </div>
              <div className="text-center p-3 bg-slate-900/50 rounded-xl">
                <p className="text-slate-500 text-[10px] mb-1">DEDUCTED</p>
                <p className="text-red-400 text-lg font-bold">{currencySymbol}{deductionsValue.toLocaleString()}</p>
              </div>
            </div>
            
            {/* Remaining Amount Box */}
            <div className={`rounded-xl p-4 text-center ${
              worker.status === 'settled' ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-orange-500/10 border border-orange-500/30'
            }`}>
              <p className="text-slate-400 text-[10px] mb-1">
                {worker.status === 'settled' ? 'SETTLED (DEBT CLEARED)' : 'REMAINING'}
              </p>
              <p className={`text-3xl font-black ${
                worker.status === 'settled' ? 'text-purple-400' : 'text-orange-500'
              }`}>
                {worker.status === 'settled' ? `${currencySymbol}0` : `${currencySymbol}${(worker.remainingAmount || 0).toLocaleString()}`}
              </p>
            </div>
            
            {/* Payment & Deduction buttons - Only show if NOT locked */}
            {!isLocked && (
              <>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <Button
                    onClick={() => onPayment(worker)}
                    className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-xs font-bold rounded-xl"
                    data-testid={`payment-btn-${worker.id}`}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    PAYMENT
                  </Button>
                  <Button
                    onClick={() => onDeduction(worker)}
                    className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white text-xs font-bold rounded-xl"
                    data-testid={`deduction-btn-${worker.id}`}
                  >
                    <Minus className="h-4 w-4 mr-2" />
                    DEDUCTION
                  </Button>
                </div>
                
                {/* Mark as Fully Paid Button */}
                {showFullyPaid && (
                  <Button
                    onClick={() => onFullyPaid(worker)}
                    className="w-full mt-3 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-slate-900 text-sm font-black rounded-xl"
                    data-testid={`fully-paid-btn-${worker.id}`}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    MARK FULLY PAID
                  </Button>
                )}
              </>
            )}
          </div>
          
          {/* Settlement Button - Only for runaway/noarrival */}
          {canSettle && (
            <Button
              onClick={() => onSettle(worker)}
              className="w-full mb-3 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white text-sm font-bold rounded-xl shadow-lg shadow-purple-500/25"
              data-testid={`settle-btn-${worker.id}`}
            >
              <DollarSign className="h-4 w-4 mr-2" />
              SETTLEMENT
            </Button>
          )}

          {/* WARRANTY SECTION - NEW */}
          <div className="bg-slate-950/50 rounded-xl p-4 mb-3 border border-violet-500/30">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-violet-400" />
                <span className="text-white text-sm font-semibold">WARRANTY / الضمان</span>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                worker.warrantyActivated 
                  ? worker.warrantyStatus === 'critical' ? 'bg-red-500/20 text-red-400' 
                    : worker.warrantyStatus === 'warning' ? 'bg-amber-500/20 text-amber-400'
                    : worker.warrantyStatus === 'completed' ? 'bg-slate-500/20 text-slate-400'
                    : 'bg-emerald-500/20 text-emerald-400'
                  : 'bg-violet-500/20 text-violet-400'
              }`}>
                {worker.warrantyActivated 
                  ? worker.warrantyStatus === 'critical' ? 'CRITICAL / حرج'
                    : worker.warrantyStatus === 'warning' ? 'WARNING / تحذير'
                    : worker.warrantyStatus === 'completed' ? 'COMPLETED / منتهي'
                    : 'ACTIVE / نشط'
                  : 'NOT ACTIVATED / غير مفعل'}
              </span>
            </div>
            
            {worker.warrantyActivated ? (
              /* Activated Warranty Display - Professional Ring Design */
              (() => {
                const totalDays = (worker.warrantyPeriod || 3) * 30;
                const daysRemaining = worker.warrantyDaysRemaining || 0;
                const elapsed = totalDays - daysRemaining;
                const progressPercent = Math.min(100, Math.max(0, (elapsed / totalDays) * 100));
                
                const getWarrantyColor = () => {
                  if (worker.warrantyStatus === 'critical') return '#EF4444';
                  if (worker.warrantyStatus === 'warning') return '#F59E0B';
                  if (worker.warrantyStatus === 'completed') return '#10B981';
                  return '#10B981';
                };
                
                return (
                  <div className="flex items-center gap-4">
                    {/* Warranty Ring */}
                    <div className="relative w-20 h-20 flex-shrink-0">
                      <svg className="w-20 h-20 -rotate-90" viewBox="0 0 64 64">
                        <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" />
                        <circle 
                          cx="32" cy="32" r="28" fill="none" 
                          stroke={getWarrantyColor()} 
                          strokeWidth="4" 
                          strokeLinecap="round"
                          strokeDasharray={`${progressPercent * 1.76} 176`}
                          className="transition-all duration-1000"
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className={`text-lg font-black ${
                          worker.warrantyStatus === 'critical' ? 'text-red-400' 
                          : worker.warrantyStatus === 'warning' ? 'text-amber-400' 
                          : 'text-emerald-400'
                        }`}>
                          {daysRemaining}
                        </span>
                        <span className="text-[9px] text-slate-500 uppercase">يوم / days</span>
                      </div>
                    </div>
                    
                    {/* Warranty Details */}
                    <div className="flex-1 space-y-2">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-slate-900/50 rounded-lg p-2">
                          <span className="text-slate-500 block text-[10px]">المدة / Period</span>
                          <span className="text-white font-bold">{worker.warrantyPeriod || 3} شهور</span>
                        </div>
                        <div className="bg-slate-900/50 rounded-lg p-2">
                          <span className="text-slate-500 block text-[10px]">الانتهاء / Expiry</span>
                          <span className="text-white font-medium">{worker.warrantyExpiryDate || 'N/A'}</span>
                        </div>
                      </div>

                      {/* Period switch (works AFTER activation — recalculates expiry) */}
                      <div className="flex items-center gap-2 bg-slate-900/30 rounded-lg p-2">
                        <span className="text-[10px] text-slate-500">تعديل المدة / Change:</span>
                        <button
                          onClick={() => {
                            if ((worker.warrantyPeriod || 3) === 3) return;
                            if (window.confirm('تغيير المدة إلى 3 شهور سيُعيد حساب تاريخ انتهاء الضمان.\nChange to 3 months? Expiry will be recalculated.')) {
                              onWarrantyPeriodChange && onWarrantyPeriodChange(worker.id, 3);
                            }
                          }}
                          className={`px-2 py-0.5 rounded text-[10px] font-bold transition-all ${
                            (worker.warrantyPeriod || 3) === 3
                              ? 'bg-violet-500 text-white'
                              : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                          }`}
                          data-testid={`warranty-3m-active-${worker.id}`}
                        >
                          3 شهور
                        </button>
                        <button
                          onClick={() => {
                            if ((worker.warrantyPeriod || 3) === 6) return;
                            if (window.confirm('تغيير المدة إلى 6 شهور سيُعيد حساب تاريخ انتهاء الضمان.\nChange to 6 months? Expiry will be recalculated.')) {
                              onWarrantyPeriodChange && onWarrantyPeriodChange(worker.id, 6);
                            }
                          }}
                          className={`px-2 py-0.5 rounded text-[10px] font-bold transition-all ${
                            (worker.warrantyPeriod || 3) === 6
                              ? 'bg-violet-500 text-white'
                              : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                          }`}
                          data-testid={`warranty-6m-active-${worker.id}`}
                        >
                          6 شهور
                        </button>
                      </div>

                      {/* Progress Bar */}
                      <div className="space-y-1">
                        <div className="flex justify-between text-[10px]">
                          <span className="text-slate-500">Progress</span>
                          <span className={`font-medium ${
                            worker.warrantyStatus === 'critical' ? 'text-red-400' 
                            : worker.warrantyStatus === 'warning' ? 'text-amber-400' 
                            : 'text-emerald-400'
                          }`}>{Math.round(progressPercent)}%</span>
                        </div>
                        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all duration-1000 ${
                              worker.warrantyStatus === 'critical' ? 'bg-gradient-to-r from-red-500 to-red-600' :
                              worker.warrantyStatus === 'warning' ? 'bg-gradient-to-r from-amber-500 to-orange-500' :
                              'bg-gradient-to-r from-emerald-500 to-green-400'
                            }`}
                            style={{ width: `${progressPercent}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()
            ) : (
              /* Not Activated - Show Activation Controls */
              <div className="space-y-3">
                {/* Period Selection */}
                <div className="flex items-center gap-2">
                  <span className="text-slate-400 text-xs">مدة الضمان / Period:</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => onWarrantyPeriodChange && onWarrantyPeriodChange(worker.id, 3)}
                      className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                        (worker.warrantyPeriod || 3) === 3 
                          ? 'bg-violet-500 text-white' 
                          : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                      }`}
                      data-testid={`warranty-3m-${worker.id}`}
                    >
                      3 شهور
                    </button>
                    <button
                      onClick={() => onWarrantyPeriodChange && onWarrantyPeriodChange(worker.id, 6)}
                      className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                        (worker.warrantyPeriod || 3) === 6 
                          ? 'bg-violet-500 text-white' 
                          : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                      }`}
                      data-testid={`warranty-6m-${worker.id}`}
                    >
                      6 شهور
                    </button>
                  </div>
                </div>
                
                {/* Activate Button */}
                {(worker.status === 'arrived' || worker.arrivalStatus === 'inside_country') ? (
                  <Button
                    onClick={() => onWarrantyActivate && onWarrantyActivate(worker.id)}
                    className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white text-sm font-bold rounded-xl"
                    data-testid={`activate-warranty-${worker.id}`}
                  >
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    تفعيل الضمان / ACTIVATE WARRANTY
                  </Button>
                ) : (
                  <div className="bg-slate-800/50 rounded-xl p-2 text-center">
                    <p className="text-slate-500 text-xs">
                      يجب وصول العامل أولاً / Worker must arrive first
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Delete Worker Button */}
          <Button
            onClick={() => onDelete(worker)}
            variant="ghost"
            className="w-full bg-slate-800/50 hover:bg-red-500/10 text-slate-400 hover:text-red-400 text-sm font-semibold rounded-xl border border-white/5"
            data-testid={`delete-worker-btn-${worker.id}`}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            DELETE WORKER
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

const SupplierProfilePage = () => {
  const { id: supplierId } = useParams();
  const navigate = useNavigate();
  const { suppliers, cases, fetchSuppliers, fetchCases } = useApp();
  
  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState(null);
  const [supplierCases, setSupplierCases] = useState([]);
  
  // Search state
  const [workerSearch, setWorkerSearch] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10; // Workers per page
  
  // Month/Year filters
  const [monthFilter, setMonthFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  
  // Search fields configuration
  const workerSearchFields = useMemo(() => [
    { key: 'workerName', weight: 1.5, threshold: 40 },
    { key: 'nationality', weight: 1.0, threshold: 60 },
    { key: 'passportNumber', weight: 1.0, threshold: 70 }
  ], []);
  
  // Modals
  const [paymentModal, setPaymentModal] = useState({ open: false, worker: null });
  const [deductionModal, setDeductionModal] = useState({ open: false, worker: null });
  const [amendmentModal, setAmendmentModal] = useState({ open: false, worker: null });
  const [workerDetailsModal, setWorkerDetailsModal] = useState({ open: false, worker: null });
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDescription, setPaymentDescription] = useState('');
  const [deductionAmount, setDeductionAmount] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Amendment form state
  const [amendmentForm, setAmendmentForm] = useState({
    amount: '',
    type: 'increase', // 'increase' or 'decrease'
    reason: 'travel_costs',
    reason_text: '',
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [amendmentLoading, setAmendmentLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchSuppliers(true), fetchCases(true)]);
    setLoading(false);
  }, [fetchSuppliers, fetchCases]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (suppliers.length > 0 && supplierId) {
      const found = suppliers.find(s => s.id === supplierId);
      setSupplier(found);
    }
  }, [suppliers, supplierId]);

  useEffect(() => {
    if (cases.length > 0 && supplierId) {
      let filtered = cases.filter(c => c.supplierId === supplierId || c.supplier_id === supplierId);
      
      // Apply month/year filters
      if (monthFilter !== 'all' || yearFilter !== 'all') {
        filtered = filtered.filter(c => {
          const createdAt = c.createdAt || c.created_at;
          if (!createdAt) return true;
          const date = new Date(createdAt);
          const monthMatch = monthFilter === 'all' || date.getMonth() === parseInt(monthFilter);
          const yearMatch = yearFilter === 'all' || date.getFullYear() === parseInt(yearFilter);
          return monthMatch && yearMatch;
        });
      }
      
      setSupplierCases(filtered);
    }
  }, [cases, supplierId, monthFilter, yearFilter]);

  // Calculate stats
  const currency = supplier?.currency || 'AED';
  const currencySymbol = currency === 'AED' ? 'AED' : '$';
  
  const allSupplierCases = cases.filter(c => c.supplierId === supplierId || c.supplier_id === supplierId);
  const totalContracts = allSupplierCases.reduce((sum, c) => sum + (c.totalCost || c.contractAmountUSD || 0), 0);
  const totalPaid = allSupplierCases.reduce((sum, c) => sum + (c.paidAmount || 0), 0);
  // Calculate remaining correctly: Total - Paid + Deductions (deductions add to what's owed)
  const totalDeductions = allSupplierCases.reduce((sum, c) => sum + (c.deductionsUSD || 0), 0);
  // Use stored remainingAmount if available, otherwise calculate
  const totalRemaining = allSupplierCases.reduce((sum, c) => {
    // If remainingAmount is stored and seems correct, use it
    const stored = c.remainingAmount || 0;
    const total = c.totalCost || c.contractAmountUSD || 0;
    const paid = c.paidAmount || 0;
    const deductions = c.deductionsUSD || 0;
    const calculated = Math.max(0, total - paid + deductions);
    // Use calculated value to ensure correctness
    return sum + calculated;
  }, 0);
  
  // Calculate debt owed
  let totalDebt = 0;
  allSupplierCases.forEach(c => {
    if (c.status === 'settled') {
      // Debt was cleared
    } else if (c.status === 'runaway') {
      totalDebt += (c.totalCost || 0);
    } else if (c.status === 'noarrival') {
      totalDebt += (c.paidAmount || 0);
    }
  });
  
  // Status counts
  const abroadCount = allSupplierCases.filter(c => c.status === 'abroad').length;
  const arrivedCount = allSupplierCases.filter(c => c.status === 'arrived').length;
  const runawayCount = allSupplierCases.filter(c => c.status === 'runaway').length;
  const noArrivalCount = allSupplierCases.filter(c => c.status === 'noarrival').length;
  const settledCount = allSupplierCases.filter(c => c.status === 'settled').length;

  // Apply advanced search to workers
  const filteredWorkers = useMemo(() => {
    if (!workerSearch || !workerSearch.trim()) return supplierCases;
    return advancedSearch(supplierCases, workerSearch, workerSearchFields);
  }, [supplierCases, workerSearch, workerSearchFields]);

  // Pagination logic
  const totalPages = Math.ceil(filteredWorkers.length / PAGE_SIZE);
  const paginatedWorkers = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredWorkers.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredWorkers, currentPage, PAGE_SIZE]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [workerSearch, monthFilter, yearFilter, supplierId]);

  // Get unique years from cases
  const years = [...new Set(allSupplierCases.map(c => {
    const createdAt = c.createdAt || c.created_at;
    return createdAt ? new Date(createdAt).getFullYear() : null;
  }).filter(Boolean))].sort((a, b) => b - a);

  // Action handlers - ALL PRESERVED AS IS
  const handleStatusChange = async (caseId, newStatus) => {
    const statusLabels = {
      'abroad': 'خارج الدولة (Abroad)',
      'arrived': 'داخل الدولة (Arrived)',
      'runaway': 'هروب (Runaway)',
      'noarrival': 'لم تصل (No Arrival)'
    };

    const extra = newStatus === 'arrived'
      ? '\nملاحظة: سيتم تفعيل الضمان وإنشاء فيزا 30 يوم تلقائياً إن لم تكن موجودة.'
      : '';
    if (!window.confirm(`هل أنت متأكد من تغيير الحالة إلى "${statusLabels[newStatus] || newStatus}"؟${extra}`)) {
      return;
    }
    
    setActionLoading(true);
    try {
      const payload = { status: newStatus };
      if (newStatus === 'arrived') {
        payload.arrivalDate = new Date().toISOString().split('T')[0];
      }
      
      await apiPost(`/api/cases/${caseId}/status`, payload);
      toast.success(`Status updated successfully`);
      await loadData();
    } catch (error) {
      console.error('Status change error:', error);
      const errorMsg = typeof error.message === 'string' ? error.message : 'An error occurred';
      toast.error(errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePayment = async () => {
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setActionLoading(true);
    try {
      await apiPost(`/api/cases/${paymentModal.worker.id}/payment`, {
        amount,
        description: (paymentDescription || '').trim(),
      });
      toast.success(`Payment of ${currencySymbol}${amount.toLocaleString()} added successfully`);
      setPaymentModal({ open: false, worker: null });
      setPaymentAmount('');
      setPaymentDescription('');
      await loadData();
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeduction = async () => {
    const amount = parseFloat(deductionAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setActionLoading(true);
    try {
      await apiPost(`/api/cases/${deductionModal.worker.id}/deduction`, { amount });
      toast.success(`Deduction of ${currencySymbol}${amount.toLocaleString()} added successfully`);
      setDeductionModal({ open: false, worker: null });
      setDeductionAmount('');
      await loadData();
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFullyPaid = async (worker) => {
    if (!window.confirm(`Are you sure you want to pay the full remaining amount (${currencySymbol}${worker.remainingAmount?.toLocaleString()})?`)) {
      return;
    }
    
    setActionLoading(true);
    try {
      await apiPost(`/api/cases/${worker.id}/payment`, { amount: worker.remainingAmount });
      toast.success('Full payment completed successfully');
      await loadData();
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSettle = async (worker) => {
    if (!window.confirm('Are you sure you want to settle this debt? The outstanding amount will be cleared.')) {
      return;
    }
    
    setActionLoading(true);
    try {
      await apiPost(`/api/cases/${worker.id}/status`, { status: 'settled' });
      toast.success('Debt settled successfully');
      await loadData();
    } catch (error) {
      console.error('Settle error:', error);
      const errorMsg = typeof error.message === 'string' ? error.message : 'An error occurred';
      toast.error(errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (worker) => {
    if (!window.confirm(`Are you sure you want to delete worker "${worker.workerName}"? This action cannot be undone.`)) {
      return;
    }
    
    setActionLoading(true);
    try {
      await apiDelete(`/api/cases/${worker.id}`);
      toast.success('Worker deleted successfully');
      await loadData();
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle adding contract amendment
  const handleAddAmendment = async () => {
    if (!amendmentForm.amount || parseFloat(amendmentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setAmendmentLoading(true);
    try {
      // Calculate the actual amount based on type (positive for increase, negative for decrease)
      const actualAmount = amendmentForm.type === 'decrease' 
        ? -Math.abs(parseFloat(amendmentForm.amount))
        : Math.abs(parseFloat(amendmentForm.amount));
      
      await apiPost(`/api/cases/${amendmentModal.worker.id}/amendment`, {
        amount: actualAmount,
        type: amendmentForm.type,
        reason: amendmentForm.reason,
        reason_text: amendmentForm.reason_text,
        notes: amendmentForm.notes,
        date: amendmentForm.date
      });
      toast.success(amendmentForm.type === 'decrease' ? 'تم إنقاص قيمة العقد / Contract decreased' : 'تم إضافة ملحق العقد / Amendment added');
      setAmendmentModal({ open: false, worker: null });
      setAmendmentForm({
        amount: '',
        type: 'increase',
        reason: 'travel_costs',
        reason_text: '',
        notes: '',
        date: new Date().toISOString().split('T')[0]
      });
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to add amendment');
    } finally {
      setAmendmentLoading(false);
    }
  };

  // Handle deleting contract amendment
  const handleDeleteAmendment = async (amendmentId) => {
    if (!window.confirm('Are you sure you want to delete this amendment?')) {
      return;
    }
    
    setAmendmentLoading(true);
    try {
      await apiDelete(`/api/cases/${amendmentModal.worker.id}/amendment/${amendmentId}`);
      toast.success('Amendment deleted successfully');
      await loadData();
      // Refresh modal worker data
      const updatedWorker = cases.find(c => c.id === amendmentModal.worker.id);
      if (updatedWorker) {
        setAmendmentModal({ open: true, worker: updatedWorker });
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete amendment');
    } finally {
      setAmendmentLoading(false);
    }
  };

  // Get reason label
  const getReasonLabel = (reason) => {
    const labels = {
      'travel_costs': 'تكاليف سفر (Travel Costs)',
      'visa_fees': 'رسوم تأشيرة (Visa Fees)',
      'medical_fees': 'رسوم طبية (Medical Fees)',
      'other': 'أخرى (Other)'
    };
    return labels[reason] || reason;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="supplier-profile-loading">
        <Skeleton className="h-8 w-48 bg-slate-800" />
        <Skeleton className="h-32 rounded-2xl bg-slate-800" />
        <div className="grid grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl bg-slate-800" />
          ))}
        </div>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="text-center py-20" data-testid="supplier-not-found">
        <p className="text-slate-500 text-lg mb-4">Supplier not found</p>
        <Button onClick={() => navigate('/suppliers')} variant="outline" className="border-white/10 text-white rounded-xl">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Suppliers
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="supplier-profile-page">
      {/* Back Button */}
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
        <Button 
          onClick={() => navigate('/suppliers')} 
          variant="ghost" 
          className="text-slate-400 hover:text-white rounded-xl"
          data-testid="back-to-suppliers"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Suppliers
        </Button>
      </motion.div>

      {/* Supplier Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        className="relative bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 text-center overflow-hidden"
      >
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-300 via-white/30 to-orange-300" />
        
        <Button
          onClick={() => navigate('/suppliers')}
          variant="ghost"
          className="absolute top-4 left-4 bg-white/20 border border-white/30 text-white text-sm hover:bg-white/30 rounded-xl"
          size="sm"
        >
          <Edit className="h-3.5 w-3.5 mr-1.5" />
          Edit
        </Button>
        
        <div className="h-16 w-16 mx-auto mb-3 rounded-xl bg-white/20 flex items-center justify-center text-2xl font-bold text-white">
          {supplier.name?.charAt(0) || 'S'}
        </div>
        
        <h1 className="text-white text-2xl font-bold">{supplier.name}</h1>
        <p className="text-white/80 text-sm mt-2 flex items-center justify-center gap-3">
          {supplier.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{supplier.phone}</span>}
          {supplier.country && <span className="inline-flex items-center gap-1"><Globe className="h-3.5 w-3.5" />{supplier.country}</span>}
        </p>
        
        <div className="mt-4">
          <span className="bg-white/20 border border-white/30 text-white px-4 py-1.5 rounded-full text-sm font-semibold">
            {currencySymbol} {currency}
          </span>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <motion.div 
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
        variants={containerVariants}
        initial="hidden"
        animate="show"
      >
        <motion.div variants={itemVariants}>
          <Card className="bg-slate-900/60 border border-white/5 rounded-xl">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-slate-500 text-[10px] sm:text-xs mb-1 uppercase tracking-wider">Workers</p>
              <p className="text-orange-500 text-lg sm:text-xl font-bold">{allSupplierCases.length}</p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div variants={itemVariants}>
          <Card className="bg-slate-900/60 border border-white/5 rounded-xl">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-slate-500 text-[10px] sm:text-xs mb-1 uppercase tracking-wider">Contracts</p>
              <p className="text-white text-sm sm:text-lg font-bold truncate" title={`${currencySymbol}${totalContracts.toLocaleString()}`}>
                {currencySymbol}{totalContracts >= 1000000 ? `${(totalContracts/1000000).toFixed(1)}M` : totalContracts >= 1000 ? `${(totalContracts/1000).toFixed(1)}K` : totalContracts.toLocaleString()}
              </p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div variants={itemVariants}>
          <Card className="bg-slate-900/60 border border-white/5 rounded-xl">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-slate-500 text-[10px] sm:text-xs mb-1 uppercase tracking-wider">Paid</p>
              <p className="text-emerald-400 text-sm sm:text-lg font-bold truncate" title={`${currencySymbol}${totalPaid.toLocaleString()}`}>
                {currencySymbol}{totalPaid >= 1000000 ? `${(totalPaid/1000000).toFixed(1)}M` : totalPaid >= 1000 ? `${(totalPaid/1000).toFixed(1)}K` : totalPaid.toLocaleString()}
              </p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div variants={itemVariants}>
          <Card className="bg-slate-900/60 border border-white/5 rounded-xl">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-slate-500 text-[10px] sm:text-xs mb-1 uppercase tracking-wider">Remaining</p>
              <p className="text-orange-500 text-sm sm:text-lg font-bold truncate" title={`${currencySymbol}${totalRemaining.toLocaleString()}`}>
                {currencySymbol}{totalRemaining >= 1000000 ? `${(totalRemaining/1000000).toFixed(1)}M` : totalRemaining >= 1000 ? `${(totalRemaining/1000).toFixed(1)}K` : totalRemaining.toLocaleString()}
              </p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div variants={itemVariants}>
          <Card className="bg-slate-900/60 border border-white/5 rounded-xl">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-slate-500 text-[10px] sm:text-xs mb-1 uppercase tracking-wider">Deductions</p>
              <p className="text-amber-400 text-sm sm:text-lg font-bold truncate" title={`${currencySymbol}${totalDeductions.toLocaleString()}`}>
                {currencySymbol}{totalDeductions >= 1000000 ? `${(totalDeductions/1000000).toFixed(1)}M` : totalDeductions >= 1000 ? `${(totalDeductions/1000).toFixed(1)}K` : totalDeductions.toLocaleString()}
              </p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div variants={itemVariants}>
          <Card className="bg-slate-900/60 border border-white/5 rounded-xl">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-slate-500 text-[10px] sm:text-xs mb-1 uppercase tracking-wider">Debt</p>
              <p className="text-red-400 text-sm sm:text-lg font-bold truncate" title={`${currencySymbol}${totalDebt.toLocaleString()}`}>
                {currencySymbol}{totalDebt >= 1000000 ? `${(totalDebt/1000000).toFixed(1)}M` : totalDebt >= 1000 ? `${(totalDebt/1000).toFixed(1)}K` : totalDebt.toLocaleString()}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>

      {/* Status Breakdown */}
      <motion.div 
        className="flex gap-2 flex-wrap"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <span className="bg-blue-500/10 border border-blue-500/30 px-4 py-2 rounded-xl text-blue-400 text-sm font-semibold">
          Abroad: {abroadCount}
        </span>
        <span className="bg-emerald-500/10 border border-emerald-500/30 px-4 py-2 rounded-xl text-emerald-400 text-sm font-semibold">
          Arrived: {arrivedCount}
        </span>
        <span className="bg-red-500/10 border border-red-500/30 px-4 py-2 rounded-xl text-red-400 text-sm font-semibold">
          Runaway: {runawayCount}
        </span>
        <span className="bg-amber-500/10 border border-amber-500/30 px-4 py-2 rounded-xl text-amber-400 text-sm font-semibold">
          No Arrival: {noArrivalCount}
        </span>
        {settledCount > 0 && (
          <span className="bg-purple-500/10 border border-purple-500/30 px-4 py-2 rounded-xl text-purple-400 text-sm font-semibold">
            Settled: {settledCount}
          </span>
        )}
      </motion.div>

      {/* Filters */}
      <motion.div 
        className="flex gap-4 items-center flex-wrap"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        {/* Advanced Worker Search */}
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            value={workerSearch}
            onChange={(e) => setWorkerSearch(e.target.value)}
            placeholder="🔍 بحث عن العاملة / Search worker..."
            className="pl-10 pr-10 h-10 bg-gradient-to-r from-slate-900/80 to-slate-800/80 border-white/10 text-white placeholder:text-white/30 rounded-xl focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
          />
          {workerSearch && (
            <button
              onClick={() => setWorkerSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Label className="text-slate-400 text-sm">Month:</Label>
          <Select value={monthFilter} onValueChange={setMonthFilter}>
            <SelectTrigger className="w-[130px] bg-slate-900/60 border-white/10 text-white rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-white/10 rounded-xl">
              <SelectItem value="all" className="text-white focus:bg-white/10">All</SelectItem>
              {[...Array(12)].map((_, i) => (
                <SelectItem key={i} value={String(i)} className="text-white focus:bg-white/10">
                  {new Date(2000, i).toLocaleString('en', { month: 'long' })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <Label className="text-slate-400 text-sm">Year:</Label>
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger className="w-[100px] bg-slate-900/60 border-white/10 text-white rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-white/10 rounded-xl">
              <SelectItem value="all" className="text-white focus:bg-white/10">All</SelectItem>
              {years.map(year => (
                <SelectItem key={year} value={String(year)} className="text-white focus:bg-white/10">{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <span className="text-slate-500 text-sm">
          Showing {supplierCases.length} workers
        </span>
        
        {/* Search Results Info */}
        {workerSearch && (
          <div className="flex items-center gap-2 text-xs">
            <Sparkles className="h-3 w-3 text-cyan-400" />
            <span className="text-cyan-400">
              {filteredWorkers.length > 0 
                ? `Found ${filteredWorkers.length} result${filteredWorkers.length > 1 ? 's' : ''} / تم العثور على ${filteredWorkers.length} نتيجة`
                : 'No results / لا توجد نتائج'}
            </span>
          </div>
        )}
      </motion.div>

      {/* Workers List */}
      {filteredWorkers.length === 0 ? (
        <div className="text-center py-10">
          <div className="h-16 w-16 mx-auto mb-4 rounded-2xl bg-slate-800 flex items-center justify-center">
            <Users className="h-8 w-8 text-slate-600" />
          </div>
          <p className="text-slate-500">
            {workerSearch ? 'No workers found for this search / لا توجد نتائج للبحث' : 'No workers found for this filter'}
          </p>
        </div>
      ) : (
        <>
          <motion.div 
            className="space-y-5"
            variants={containerVariants}
            initial="hidden"
            animate="show"
          >
            {paginatedWorkers.map(worker => (
              <WorkerCard
                key={worker.id}
                worker={worker}
                currencySymbol={currencySymbol}
                onPayment={(w) => setPaymentModal({ open: true, worker: w })}
                onDeduction={(w) => setDeductionModal({ open: true, worker: w })}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
                onFullyPaid={handleFullyPaid}
                onSettle={handleSettle}
                onAmendment={(w) => setAmendmentModal({ open: true, worker: w })}
                onViewDetails={(w) => setWorkerDetailsModal({ open: true, worker: w })}
                onWarrantyActivate={async (workerId) => {
                  try {
                    const response = await apiPost(`/api/cases/${workerId}/activate-warranty`, {});
                    if (response.success) {
                      toast.success('تم تفعيل الضمان بنجاح / Warranty activated');
                      fetchCases();
                    }
                  } catch (error) {
                    toast.error('فشل تفعيل الضمان / Failed to activate warranty');
                  }
                }}
                onWarrantyPeriodChange={async (workerId, period) => {
                  try {
                    await apiPut(`/api/cases/${workerId}/warranty-period?warranty_period=${period}`);
                    toast.success(`تم تحديث المدة إلى ${period} شهور`);
                    fetchCases();
                  } catch (error) {
                    toast.error('فشل تحديث المدة');
                  }
                }}
                searchQuery={workerSearch}
              />
            ))}
          </motion.div>
          
          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={filteredWorkers.length}
            pageSize={PAGE_SIZE}
            isLoading={loading}
          />
        </>
      )}

      {/* Payment Modal */}
      <Dialog open={paymentModal.open} onOpenChange={(open) => !open && setPaymentModal({ open: false, worker: null })}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-emerald-500" />
              Add Payment
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-slate-400 text-sm">
              {paymentModal.worker?.workerName} - Remaining: {currencySymbol}{paymentModal.worker?.remainingAmount?.toLocaleString()}
            </p>
            <div className="space-y-2">
              <Label className="text-slate-300">Amount ({currency})</Label>
              <Input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="Enter amount"
                className="bg-slate-950/50 border-white/10 text-white rounded-xl"
                data-testid="payment-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Description / وصف الدفعة <span className="text-slate-500 text-xs">(optional)</span></Label>
              <Input
                type="text"
                value={paymentDescription}
                onChange={(e) => setPaymentDescription(e.target.value)}
                placeholder="e.g. دفعة أولى، تسوية تذكرة طيران، رسوم تأشيرة"
                maxLength={200}
                className="bg-slate-950/50 border-white/10 text-white rounded-xl"
                data-testid="payment-description-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setPaymentModal({ open: false, worker: null }); setPaymentDescription(''); }} className="border-white/10 text-slate-300 rounded-xl">
              Cancel
            </Button>
            <Button 
              onClick={handlePayment} 
              disabled={actionLoading}
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl"
              data-testid="confirm-payment-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deduction Modal */}
      <Dialog open={deductionModal.open} onOpenChange={(open) => !open && setDeductionModal({ open: false, worker: null })}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Minus className="h-5 w-5 text-red-500" />
              Add Deduction
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-400 text-sm mb-4">
              {deductionModal.worker?.workerName}
            </p>
            <div className="space-y-2">
              <Label className="text-slate-300">Deduction Amount ({currency})</Label>
              <Input
                type="number"
                value={deductionAmount}
                onChange={(e) => setDeductionAmount(e.target.value)}
                placeholder="Enter deduction amount"
                className="bg-slate-950/50 border-white/10 text-white rounded-xl"
                data-testid="deduction-amount-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeductionModal({ open: false, worker: null })} className="border-white/10 text-slate-300 rounded-xl">
              Cancel
            </Button>
            <Button 
              onClick={handleDeduction} 
              disabled={actionLoading}
              className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl"
              data-testid="confirm-deduction-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Deduction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contract Amendment Modal */}
      <Dialog open={amendmentModal.open} onOpenChange={(open) => !open && setAmendmentModal({ open: false, worker: null })}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="h-5 w-5 text-amber-500" />
              ملحقات العقد (Contract Amendments)
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Worker Info */}
            <div className="bg-slate-800/50 rounded-xl p-3 border border-white/5">
              <p className="text-white font-medium">{amendmentModal.worker?.workerName}</p>
              <div className="flex justify-between mt-2 text-sm">
                <span className="text-slate-400">Original Contract:</span>
                <span className="text-white">{currencySymbol}{(amendmentModal.worker?.originalTotalCost || amendmentModal.worker?.totalCost || 0).toLocaleString()}</span>
              </div>
              {(amendmentModal.worker?.totalAmendments || 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-amber-400">Total Amendments:</span>
                  <span className="text-amber-400">+{currencySymbol}{(amendmentModal.worker?.totalAmendments || 0).toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-bold border-t border-white/10 mt-2 pt-2">
                <span className="text-white">Current Total:</span>
                <span className="text-white">{currencySymbol}{(amendmentModal.worker?.totalCost || 0).toLocaleString()}</span>
              </div>
            </div>
            
            {/* Existing Amendments List */}
            {(amendmentModal.worker?.contractAmendments || []).length > 0 && (
              <div className="space-y-2">
                <p className="text-slate-400 text-sm font-medium">الملحقات الحالية (Existing Amendments):</p>
                {(amendmentModal.worker?.contractAmendments || []).map((amendment, index) => {
                  const isDecrease = amendment.amount < 0 || amendment.type === 'decrease';
                  const displayAmount = Math.abs(amendment.amount || 0);
                  return (
                    <div key={amendment.id || index} className={`bg-slate-800/30 rounded-lg p-3 border ${isDecrease ? 'border-red-500/20' : 'border-emerald-500/20'}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            {isDecrease ? (
                              <span className="text-red-400 font-bold flex items-center gap-1">
                                <Minus className="h-3 w-3" />
                                {currencySymbol}{displayAmount.toLocaleString()}
                              </span>
                            ) : (
                              <span className="text-emerald-400 font-bold flex items-center gap-1">
                                <Plus className="h-3 w-3" />
                                {currencySymbol}{displayAmount.toLocaleString()}
                              </span>
                            )}
                            <span className="text-slate-500 text-xs">{amendment.date}</span>
                          </div>
                          <p className="text-slate-300 text-sm mt-1">{getReasonLabel(amendment.reason)}</p>
                          {amendment.reason === 'other' && amendment.reason_text && (
                            <p className="text-slate-400 text-xs">{amendment.reason_text}</p>
                          )}
                          {amendment.notes && (
                            <p className="text-slate-500 text-xs mt-1">{amendment.notes}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteAmendment(amendment.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                          disabled={amendmentLoading}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Add New Amendment Form */}
            <div className="border-t border-white/10 pt-4">
              <p className="text-slate-400 text-sm font-medium mb-3">تعديل قيمة العقد (Contract Amendment):</p>
              
              <div className="space-y-3">
                {/* Amendment Type Selection */}
                <div>
                  <Label className="text-slate-400 text-xs">نوع التعديل (Amendment Type)</Label>
                  <div className="flex gap-2 mt-2">
                    <Button
                      type="button"
                      variant={amendmentForm.type === 'increase' ? 'default' : 'outline'}
                      onClick={() => setAmendmentForm(prev => ({ ...prev, type: 'increase' }))}
                      className={`flex-1 ${amendmentForm.type === 'increase' 
                        ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400 hover:bg-emerald-500/30' 
                        : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700'}`}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      زيادة (Increase)
                    </Button>
                    <Button
                      type="button"
                      variant={amendmentForm.type === 'decrease' ? 'default' : 'outline'}
                      onClick={() => setAmendmentForm(prev => ({ ...prev, type: 'decrease' }))}
                      className={`flex-1 ${amendmentForm.type === 'decrease' 
                        ? 'bg-red-500/20 border-red-500 text-red-400 hover:bg-red-500/30' 
                        : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700'}`}
                    >
                      <Minus className="h-4 w-4 mr-2" />
                      إنقاص (Decrease)
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-400 text-xs">المبلغ (Amount)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={amendmentForm.amount}
                      onChange={(e) => setAmendmentForm(prev => ({ ...prev, amount: e.target.value }))}
                      className={`bg-slate-800 border-slate-700 text-white mt-1 ${amendmentForm.type === 'decrease' ? 'border-red-500/50' : 'border-emerald-500/50'}`}
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">التاريخ (Date)</Label>
                    <Input
                      type="date"
                      value={amendmentForm.date}
                      onChange={(e) => setAmendmentForm(prev => ({ ...prev, date: e.target.value }))}
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-400 text-xs">السبب (Reason)</Label>
                  <Select 
                    value={amendmentForm.reason} 
                    onValueChange={(value) => setAmendmentForm(prev => ({ ...prev, reason: value }))}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="travel_costs">تكاليف سفر (Travel Costs)</SelectItem>
                      <SelectItem value="visa_fees">رسوم تأشيرة (Visa Fees)</SelectItem>
                      <SelectItem value="medical_fees">رسوم طبية (Medical Fees)</SelectItem>
                      <SelectItem value="other">أخرى (Other)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {amendmentForm.reason === 'other' && (
                  <div>
                    <Label className="text-slate-400 text-xs">تفاصيل السبب (Reason Details)</Label>
                    <Input
                      type="text"
                      placeholder="Enter reason..."
                      value={amendmentForm.reason_text}
                      onChange={(e) => setAmendmentForm(prev => ({ ...prev, reason_text: e.target.value }))}
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                )}
                
                <div>
                  <Label className="text-slate-400 text-xs">ملاحظات (Notes) - اختياري</Label>
                  <Input
                    type="text"
                    placeholder="Optional notes..."
                    value={amendmentForm.notes}
                    onChange={(e) => setAmendmentForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setAmendmentModal({ open: false, worker: null })}
              className="text-slate-400 hover:text-white"
            >
              إغلاق (Close)
            </Button>
            <Button
              onClick={handleAddAmendment}
              disabled={amendmentLoading || !amendmentForm.amount}
              className={`text-white ${amendmentForm.type === 'decrease' 
                ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700' 
                : 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700'}`}
            >
              {amendmentLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : amendmentForm.type === 'decrease' ? (
                <>
                  <Minus className="h-4 w-4 mr-2" />
                  إنقاص قيمة العقد (Decrease)
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  زيادة قيمة العقد (Increase)
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Worker Details Modal - NEW ENHANCED VIEW */}
      <WorkerDetailsModal
        open={workerDetailsModal.open}
        onClose={() => setWorkerDetailsModal({ open: false, worker: null })}
        worker={workerDetailsModal.worker}
        currencySymbol={currencySymbol}
        onPayment={(w) => {
          setWorkerDetailsModal({ open: false, worker: null });
          setPaymentModal({ open: true, worker: w });
        }}
        onDeduction={(w) => {
          setWorkerDetailsModal({ open: false, worker: null });
          setDeductionModal({ open: true, worker: w });
        }}
        onStatusChange={(workerId, status) => {
          setWorkerDetailsModal({ open: false, worker: null });
          handleStatusChange(workerId, status);
        }}
        onDelete={(w) => {
          setWorkerDetailsModal({ open: false, worker: null });
          handleDelete(w);
        }}
        onFullyPaid={(w) => {
          setWorkerDetailsModal({ open: false, worker: null });
          handleFullyPaid(w);
        }}
        onSettle={(w) => {
          setWorkerDetailsModal({ open: false, worker: null });
          handleSettle(w);
        }}
        onAmendment={(w) => {
          setWorkerDetailsModal({ open: false, worker: null });
          setAmendmentModal({ open: true, worker: w });
        }}
        onWarrantyActivate={async (workerId) => {
          try {
            const response = await apiPost(`/api/cases/${workerId}/activate-warranty`, {});
            if (response.success) {
              toast.success('تم تفعيل الضمان بنجاح / Warranty activated successfully');
              fetchCases();
              // Update modal worker data
              setWorkerDetailsModal(prev => ({
                ...prev,
                worker: response.worker
              }));
            }
          } catch (error) {
            toast.error('فشل تفعيل الضمان / Failed to activate warranty');
            console.error('Warranty activation error:', error);
          }
        }}
        onWarrantyPeriodChange={async (workerId, period) => {
          try {
            const response = await apiPut(`/api/cases/${workerId}/warranty-period?warranty_period=${period}`);
            if (response) {
              toast.success(`تم تحديث مدة الضمان إلى ${period} شهور / Warranty period updated to ${period} months`);
              fetchCases();
              // Update modal worker data
              setWorkerDetailsModal(prev => ({
                ...prev,
                worker: response
              }));
            }
          } catch (error) {
            toast.error('فشل تحديث مدة الضمان / Failed to update warranty period');
            console.error('Warranty period update error:', error);
          }
        }}
      />

      {/* Loading Overlay */}
      {actionLoading && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-900 rounded-2xl p-6 flex items-center gap-3 border border-white/10">
            <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
            <span className="text-white">Processing...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierProfilePage;
